import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, ArrowRight, ChevronDown, ChevronUp, Coins, FileDown, KeyRound, Layers, Loader2, Repeat, ShieldCheck, SlidersHorizontal, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { DarkCard, ScoreGauge, formatPeriod } from './RoeLeanOverview';
import TransferWeightsDialog from './TransferWeightsDialog';
import TransferEvidenceImportDialog from './TransferEvidenceImportDialog';
import TransferLearnersTable from './TransferLearnersTable';
import MirrorFeedDialog from './MirrorFeedDialog';
import CapabilityHeatmap from './CapabilityHeatmap';
import CalibrationPanel from './CalibrationPanel';
import ValueUnitsDialog from './ValueUnitsDialog';
import CohortContextDialog from './CohortContextDialog';

const BAND_CHIP = {
  green: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  red: 'bg-rose-500/10 border-rose-500/30 text-rose-300',
};
const EC_CHIP = { High: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30', Medium: 'bg-amber-500/15 text-amber-300 border-amber-500/30', Low: 'bg-rose-500/15 text-rose-300 border-rose-500/30' };
const NODE_META = {
  potential: { label: 'Potential', q: 'What potential is available? (C × M × R)' },
  capability: { label: 'Capability', q: 'Can they do it?' },
  motivation: { label: 'Motivation', q: 'Do they want to build / apply it?' },
  confidence: { label: 'Confidence', q: 'Do they believe they can?' },
  application: { label: 'Application', q: 'Do they use it when there is an opportunity?' },
  behavior: { label: 'Behavior', q: 'Has it become a sustained way of working?' },
  performance: { label: 'Performance', q: 'Are the KPIs moving?' },
};

export function EvidenceConfidencePill({ ec, compact }) {
  if (!ec || ec.score == null) return <span className="text-[9px] uppercase tracking-wide text-slate-600" data-testid="ec-pill-none">No evidence</span>;
  return (
    <span className={cn('inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full border', EC_CHIP[ec.label])} title={`Evidence confidence ${Math.round(ec.score)}/100 — how much to trust this determination`} data-testid="ec-pill">
      <ShieldCheck className="h-2.5 w-2.5" />{compact ? ec.label : `${ec.label} evidence · ${Math.round(ec.score)}`}
    </span>
  );
}

function Node({ id, node, extra }) {
  const meta = NODE_META[id];
  const insufficient = id === 'application' && node.score == null && extra?.states?.insufficient_opportunity;
  return (
    <div className="flex items-center gap-3 min-w-0" data-testid={`th-node-${id}`}>
      {node.score == null
        ? <div className="w-16 h-16 rounded-full ring-2 ring-dashed ring-slate-600 flex items-center justify-center text-[10px] text-slate-500 text-center px-1">{insufficient ? 'No opportunity' : id === 'performance' ? 'No KPIs' : id === 'potential' ? 'No C/M/R' : 'No data'}</div>
        : <ScoreGauge score={node.score} size={64} />}
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-sm font-bold text-slate-100">{meta.label}</p>
          {node.band && <span className={cn('px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase border', BAND_CHIP[node.band])} data-testid={`th-band-${id}`}>{node.band}</span>}
        </div>
        <p className="text-[11px] text-slate-500">{meta.q}</p>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {id !== 'capability' && <EvidenceConfidencePill ec={node.evidence_confidence} />}
          {id === 'application' && extra?.rate != null && <span className="text-[10px] text-slate-400 tabular-nums" data-testid="th-application-rate">Rate {Math.round(extra.rate)}% · {extra.applied}/{extra.relevant} opportunities</span>}
          {id === 'potential' && node.detail && (
            <span className="text-[10px] text-slate-400 tabular-nums" data-testid="th-potential-detail" title="Raw Potential = C × M · Retained = Raw × R · at-risk = Raw − Retained · ΔC = learning uplift (post − pre)">
              C {node.detail.c ?? '—'} · M {node.detail.m ?? '—'} · R {node.detail.r ?? '—'} → Raw {node.detail.raw ?? '—'} · Retained {node.detail.retained ?? '—'}{node.detail.at_risk != null ? ` · at-risk ${node.detail.at_risk}` : ''}{node.detail.learning_delta_c != null ? ` · ΔC +${Math.round(node.detail.learning_delta_c)}` : ''}{node.as_of ? ` · as of ${formatPeriod(node.as_of)}` : ''}
            </span>
          )}
          {id === 'confidence' && node.expected != null && (
            <span className={cn('text-[10px] tabular-nums', node.alignment_gap != null && Math.abs(node.alignment_gap) > 10 ? (node.alignment_gap < 0 ? 'text-amber-300' : 'text-rose-300') : 'text-slate-400')} data-testid="th-confidence-alignment" title="Expected confidence from demonstrated capability, mastery and context burden vs observed confidence">
              Expected {Math.round(node.expected)} · gap {node.alignment_gap > 0 ? '+' : ''}{node.alignment_gap}{node.alignment_counts ? ` · ${node.alignment_counts.under_confident || 0} under / ${node.alignment_counts.over_confident || 0} over` : ''}
            </span>
          )}
          {id === 'performance' && node.score != null && <span className="text-[10px] text-slate-400 tabular-nums" data-testid="th-performance-kpis">{node.n} KPI{node.n === 1 ? '' : 's'} · scaled to org history{node.as_of ? ` · as of ${formatPeriod(node.as_of)}` : ''}</span>}
        </div>
      </div>
    </div>
  );
}

function TransferArrow({ t, testId }) {
  if (!t) return <ArrowRight className="h-5 w-5 text-slate-700 shrink-0" />;
  const tone = t.ratio >= 98 ? 'text-emerald-300' : t.ratio >= 85 ? 'text-slate-300' : t.ratio >= 70 ? 'text-amber-300' : 'text-rose-300';
  const label = t.ratio >= 100 ? 'Amplifying' : t.ratio >= 95 ? 'Preserved' : 'Leaking';
  return (
    <div className="flex flex-col items-center shrink-0 px-1" title={`Stage transfer ${t.ratio}% · ${t.gap > 0 ? '+' : ''}${t.gap} pts`} data-testid={testId}>
      <ArrowRight className="h-4 w-4 text-slate-600" />
      <span className={cn('text-xs font-bold tabular-nums mt-0.5', tone)}>{Math.round(t.ratio)}%</span>
      <span className="text-[9px] uppercase tracking-wide text-slate-500">{label} · {t.gap > 0 ? '+' : ''}{Math.round(t.gap)}</span>
    </div>
  );
}

function InfluenceStrip({ influence }) {
  const tone = (l) => (!l ? 'text-slate-500' : l.startsWith('Strong') ? 'text-cyan-300' : l.startsWith('Moderate') ? 'text-teal-300' : 'text-slate-400');
  return (
    <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#1f2a44] p-3" data-testid="th-influence">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1.5"><Repeat className="h-3 w-3" /> Influence · lagged month-to-month association</p>
        <span className="text-[9px] uppercase tracking-wide text-amber-300/80">Association, not causation</span>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-2">
        {influence.map((i) => (
          <div key={`${i.from}-${i.to}`} className="rounded-lg bg-[#111827] px-2.5 py-2" data-testid={`th-influence-${i.from}-${i.to}`} title={i.interpretation}>
            <p className="text-[10px] text-slate-400">{NODE_META[i.from].label} → {NODE_META[i.to].label}</p>
            <p className="text-[9px] uppercase tracking-wide text-slate-600">{i.direction}</p>
            {i.correlation == null
              ? <p className="text-[11px] text-slate-500 mt-1">Needs {i.min_pairs - i.pairs > 0 ? `${i.min_pairs - i.pairs} more` : 'varied'} transitions</p>
              : <p className={cn('text-sm font-bold tabular-nums mt-1', tone(i.label))}>{i.label} <span className="text-slate-500 font-normal">r={i.correlation.toFixed(2)} · n={i.pairs}</span></p>}
          </div>
        ))}
      </div>
    </div>
  );
}

const SEV = { Critical: 'border-rose-500/40 text-rose-200 bg-rose-500/10', Moderate: 'border-amber-500/40 text-amber-200 bg-amber-500/10', Watch: 'border-slate-500/40 text-slate-300 bg-white/5' };
const KIND = { state: 'State', transfer: 'Transfer', opportunity: 'Opportunity', evidence: 'Evidence', alignment: 'Alignment' };

// Transfer Health — Confidence · Application · Behavior with Transfer, Influence and Evidence layers.
export default function TransferHealthChain({ selectedOrg, selectedCohort, isSuperAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(null);
  const [weightsOpen, setWeightsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [showLearners, setShowLearners] = useState(false);
  const [mirrorOpen, setMirrorOpen] = useState(false);
  const [panel, setPanel] = useState(null);
  const [valueUnitsOpen, setValueUnitsOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [capability, setCapability] = useState('_all');
  const [pdfBusy, setPdfBusy] = useState(false);

  const scopeParams = useCallback(() => {
    const p = new URLSearchParams();
    if (selectedOrg && selectedOrg !== '_all') p.set('organization_id', selectedOrg);
    if (selectedCohort && selectedCohort !== '_all') p.set('cohort_id', selectedCohort);
    if (capability && capability !== '_all') p.set('capability', capability);
    return p;
  }, [selectedOrg, selectedCohort, capability]);

  const downloadBrief = async () => {
    setPdfBusy(true);
    try {
      const p = scopeParams(); if (data?.period) p.set('period', data.period);
      const r = await api.get(`/transfer-health/brief.pdf?${p}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: `transfer_brief_${data?.period || ''}.pdf` });
      a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Could not build the Transfer Brief'); }
    finally { setPdfBusy(false); }
  };

  const load = useCallback(() => {
    const p = scopeParams();
    if (period) p.set('period', period);
    setLoading(true);
    api.get(`/transfer-health/overview?${p}`).then((r) => setData(r.data)).catch(() => setData({ has_data: false })).finally(() => setLoading(false));
  }, [scopeParams, period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPeriod(null); setCapability('_all'); }, [selectedOrg, selectedCohort]);

  const toolbar = (
    <div className="flex items-center gap-2 flex-wrap">
      {data?.periods?.length > 1 && (
        <select value={data.period} onChange={(e) => setPeriod(e.target.value)} className="h-7 rounded-md bg-[#0b1220] border border-[#22304d] px-2 text-xs text-slate-200" data-testid="th-period-select">
          {data.periods.map((p) => <option key={p} value={p}>{formatPeriod(p)}</option>)}
        </select>
      )}
      {data?.capabilities?.length > 0 && (
        <select value={capability} onChange={(e) => setCapability(e.target.value)} className="h-7 rounded-md bg-[#0b1220] border border-[#22304d] px-2 text-xs text-slate-200" data-testid="th-capability-select">
          <option value="_all">All capabilities</option>
          {data.capabilities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
      <button type="button" onClick={() => setImportOpen(true)} className="h-7 inline-flex items-center gap-1 rounded-md border border-[#22304d] px-2 text-xs text-slate-300 hover:bg-white/5" data-testid="th-import-btn"><Upload className="h-3 w-3" /> Import evidence</button>
      <button type="button" onClick={() => setMirrorOpen(true)} className="h-7 inline-flex items-center gap-1 rounded-md border border-[#22304d] px-2 text-xs text-slate-300 hover:bg-white/5" data-testid="th-mirror-btn"><KeyRound className="h-3 w-3" /> Mirror feed</button>
      <button type="button" onClick={downloadBrief} disabled={pdfBusy || !data?.has_data} className="h-7 inline-flex items-center gap-1 rounded-md border border-teal-500/40 px-2 text-xs text-teal-300 hover:bg-teal-500/10 disabled:opacity-50" data-testid="th-brief-btn">{pdfBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />} Brief PDF</button>
      <button type="button" onClick={() => setWeightsOpen(true)} className="h-7 inline-flex items-center gap-1 rounded-md border border-[#22304d] px-2 text-xs text-slate-300 hover:bg-white/5" data-testid="th-weights-btn"><SlidersHorizontal className="h-3 w-3" /> Weights</button>
      <button type="button" onClick={() => setContextOpen(true)} className="h-7 inline-flex items-center gap-1 rounded-md border border-[#22304d] px-2 text-xs text-slate-300 hover:bg-white/5" data-testid="th-context-btn"><Layers className="h-3 w-3" /> Role context</button>
    </div>
  );

  return (
    <DarkCard className="p-5 space-y-4" testId="transfer-health-chain">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-[#0b1220] ring-1 ring-[#22304d] flex items-center justify-center"><Activity className="h-4 w-4 text-[#44b6d2]" /></span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Transfer Health · Value Transfer Chain</p>
            <p className="text-[11px] text-slate-500">Health of each state · % between stages is transfer · evidence pills say how much to trust each number{data?.period ? ` · ${formatPeriod(data.period)}` : ''}</p>
          </div>
        </div>
        {toolbar}
      </div>

      {loading && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div>}
      {!loading && data && !data.has_data && (
        <p className="text-xs text-slate-400 py-4" data-testid="th-empty">No transfer evidence yet for this scope. Import the Transfer Evidence CSV, or capture Confidence / Application / Behaviour signals via the ROE surveys.</p>
      )}
      {!loading && data?.has_data && (
        <>
          <div className="flex items-center gap-2 xl:gap-3 flex-wrap xl:flex-nowrap">
            <Node id="potential" node={data.nodes.potential} />
            <TransferArrow t={data.transfers.cap_conf} testId="th-transfer-cap_conf" />
            <Node id="confidence" node={data.nodes.confidence} />
            <TransferArrow t={data.transfers.conf_app} testId="th-transfer-conf_app" />
            <Node id="application" node={data.nodes.application} extra={{ rate: data.application_rate, ...data.opportunities }} />
            <TransferArrow t={data.transfers.app_beh} testId="th-transfer-app_beh" />
            <Node id="behavior" node={data.nodes.behavior} />
            <TransferArrow t={data.transfers.beh_perf} testId="th-transfer-beh_perf" />
            <Node id="performance" node={data.nodes.performance} />
          </div>
          {data.capability && <p className="text-[11px] text-teal-300" data-testid="th-capability-scope">Showing evidence tagged “{data.capability}” only.</p>}

          <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-400" data-testid="th-coverage">
            <span>{data.coverage.subjects} subjects · {data.coverage.learner_rows} learner rows{data.coverage.cohort_rows ? ` · ${data.coverage.cohort_rows} cohort-level (from ROE surveys)` : ''}</span>
            {data.opportunities.states.insufficient_opportunity > 0 && <span className="text-slate-300" data-testid="th-insufficient">{data.opportunities.states.insufficient_opportunity} with no relevant opportunity (excluded from Application, not scored 0)</span>}
            <span className="ml-auto inline-flex items-center gap-1.5">Overall evidence <EvidenceConfidencePill ec={data.evidence_confidence_avg} /></span>
            <span className="text-slate-600">weights: {data.weights_source.replace('_', ' ')}</span>
          </div>

          {data.reasoning && (
            <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#1f2a44] p-3 grid md:grid-cols-4 gap-3 text-xs" data-testid="th-reasoning">
              <div className="md:col-span-2">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Reasoning · primary constraint</p>
                <p className="text-slate-100 font-semibold" data-testid="th-reasoning-headline">{data.reasoning.headline}</p>
                <p className="text-emerald-300 mt-1"><span className="text-[9px] uppercase tracking-wide text-slate-500 mr-1">Do</span>{data.reasoning.do}</p>
                <p className="text-rose-300"><span className="text-[9px] uppercase tracking-wide text-slate-500 mr-1">Don&apos;t</span>{data.reasoning.do_not}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Expected impact</p>
                {data.reasoning.expected_impact ? <p className="text-slate-200" data-testid="th-reasoning-impact">Lift the constraint to {data.reasoning.expected_impact.if_conversion_pct}% conversion → <span className="font-semibold text-teal-300">{data.reasoning.expected_impact.gain_pts > 0 ? '+' : ''}{Math.round(data.reasoning.expected_impact.gain_pts)} pts</span> {data.reasoning.expected_impact.stage}</p> : <p className="text-slate-500">—</p>}
                <p className="mt-1 inline-flex items-center gap-1.5 text-slate-400">Evidence <EvidenceConfidencePill ec={data.reasoning.evidence} compact /></p>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Predicted vs actual</p>
                {data.reasoning.predicted_vs_actual ? <p className="text-slate-200 tabular-nums" data-testid="th-reasoning-pva">{formatPeriod(data.reasoning.predicted_vs_actual.period)} {data.reasoning.predicted_vs_actual.stage}: predicted {data.reasoning.predicted_vs_actual.predicted} · actual {data.reasoning.predicted_vs_actual.actual} · <span className={cn('font-semibold', Math.abs(data.reasoning.predicted_vs_actual.error) <= 3 ? 'text-emerald-300' : Math.abs(data.reasoning.predicted_vs_actual.error) <= 7 ? 'text-amber-300' : 'text-rose-300')}>{data.reasoning.predicted_vs_actual.error > 0 ? '+' : ''}{data.reasoning.predicted_vs_actual.error}</span></p> : <p className="text-slate-500">Needs 3+ months</p>}
              </div>
            </div>
          )}

          {data.value && (
            <div className="flex items-center justify-between gap-2 -mb-1">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Value realization · {data.value.value_basis}</p>
              <button type="button" onClick={() => setValueUnitsOpen(true)} className="h-6 inline-flex items-center gap-1 rounded-md border border-[#22304d] px-2 text-[11px] text-slate-300 hover:bg-white/5" data-testid="th-value-units-btn"><Coins className="h-3 w-3" /> Value assumptions</button>
            </div>
          )}
          {data.value && (
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-2" data-testid="th-value">
              {[['Investment', data.value.investment != null ? `$${Math.round(data.value.investment).toLocaleString()}` : '—', `${data.value.programs} programme${data.value.programs === 1 ? '' : 's'} in scope`],
                ['KPI movement', data.value.kpi_change_pct != null ? `${data.value.kpi_change_pct > 0 ? '+' : ''}${data.value.kpi_change_pct}%` : '—', `median of ${data.value.kpis_measured} KPIs, first → latest month`],
                ['Attributable benefit', data.value.attributable_benefit != null ? `$${Math.round(data.value.attributable_benefit).toLocaleString()}` : '—', `attribution ${Math.round((data.value.attribution_strength || 0) * 100)}% · ${data.value.attribution_label}`],
                ['ROI', data.value.roi_pct != null ? `${data.value.roi_pct > 0 ? '+' : ''}${data.value.roi_pct}%` : '—', '(benefit − investment) ÷ investment']].map(([label, v, note]) => (
                <div key={label} className="rounded-lg bg-[#0b1220] ring-1 ring-[#1f2a44] px-3 py-2" data-testid={`th-value-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="text-[9px] uppercase tracking-wide text-slate-500">{label}</p>
                  <p className="text-base font-bold text-slate-100 tabular-nums">{v}</p>
                  <p className="text-[10px] text-slate-500 leading-snug">{note}</p>
                </div>
              ))}
            </div>
          )}

          {data.diagnosis.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="th-diagnosis">
              {data.diagnosis.map((d, i) => (
                <span key={i} className={cn('inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px]', SEV[d.severity])} data-testid={`th-diag-${d.kind}-${d.node}`}>
                  <AlertTriangle className="h-3 w-3 shrink-0" /><span className="text-[9px] uppercase tracking-wide opacity-70">{KIND[d.kind]}</span>{d.headline}
                </span>
              ))}
            </div>
          )}

          {data.by_capability?.length > 0 && (
            <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#1f2a44] p-3" data-testid="th-by-capability">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">By capability · {formatPeriod(data.period)}</p>
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wide text-slate-500"><tr><th className="text-left py-1">Capability</th><th>n</th><th>Confidence</th><th>Application</th><th>Behavior</th></tr></thead>
                <tbody className="divide-y divide-[#1f2a44]">
                  {data.by_capability.map((b) => (
                    <tr key={b.capability} className="hover:bg-white/5 cursor-pointer" onClick={() => setCapability(b.capability)} data-testid={`th-cap-row-${b.capability}`}>
                      <td className="py-1.5 text-slate-200">{b.capability}</td><td className="text-center text-slate-500">{b.n}</td>
                      {['confidence', 'application', 'behavior'].map((k) => <td key={k} className={cn('text-center font-semibold tabular-nums', { green: 'text-emerald-300', amber: 'text-amber-300', red: 'text-rose-300' }[b.bands[k]] || 'text-slate-500')}>{b[k] == null ? '—' : Math.round(b[k])}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <InfluenceStrip influence={data.influence} />

          <div className="flex items-center gap-2 flex-wrap" data-testid="th-panel-tabs">
            {[['heatmap', 'Capability heatmap'], ['calibration', 'Weight calibration']].map(([k, label]) => (
              <button key={k} type="button" onClick={() => setPanel(panel === k ? null : k)} className={cn('h-7 px-2.5 rounded-md border text-xs', panel === k ? 'border-teal-500/50 text-teal-300 bg-teal-500/10' : 'border-[#22304d] text-slate-400 hover:text-slate-200')} data-testid={`th-panel-${k}`}>{label}</button>
            ))}
          </div>
          {panel === 'heatmap' && <CapabilityHeatmap organizationId={selectedOrg} period={data.period} onPick={(c) => setCapability(c)} />}
          {panel === 'calibration' && <CalibrationPanel organizationId={selectedOrg} cohortId={selectedCohort} onApplied={load} />}

          <button type="button" onClick={() => setShowLearners((s) => !s)} className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1" data-testid="th-learners-toggle">
            {showLearners ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}{showLearners ? 'Hide' : 'Show'} learner detail for {formatPeriod(data.period)}
          </button>
          {showLearners && <TransferLearnersTable organizationId={selectedOrg} cohortId={selectedCohort} period={data.period} capability={capability} />}
        </>
      )}

      <TransferWeightsDialog open={weightsOpen} onOpenChange={setWeightsOpen} organizationId={selectedOrg} canGlobal={isSuperAdmin} onSaved={load} />
      <TransferEvidenceImportDialog open={importOpen} onOpenChange={setImportOpen} organizationId={selectedOrg} cohortId={selectedCohort} onImported={load} />
      <MirrorFeedDialog open={mirrorOpen} onOpenChange={setMirrorOpen} organizationId={selectedOrg} />
      <ValueUnitsDialog open={valueUnitsOpen} onOpenChange={setValueUnitsOpen} organizationId={selectedOrg} onSaved={load} />
      <CohortContextDialog open={contextOpen} onOpenChange={setContextOpen} organizationId={selectedOrg} onSaved={load} />
    </DarkCard>
  );
}
